import type {
  DiscordChannelMapT,
  DiscordChannelT,
  DiscordInfo,
  IMPORT_STATE,
  ImportHistoryItemT,
} from '@/features/Setting/Integrations/providers/types';
import { defaultAxios } from '@/services/index';

export type DiscordWorkspaceResponse = {
  alias: string;
  guild_info?: {
    name: string;
  };
  state: IMPORT_STATE;
  history?: ImportHistoryItemT[];
  channels: DiscordChannelT[];
  period: number;
  isDuplicated?: boolean;
};

const discordService = {
  async createDiscordInfo(
    code: string,
    workspaceAlias: string,
    redirect_uri: string,
  ): Promise<DiscordInfo> {
    const res = await defaultAxios.post<DiscordWorkspaceResponse>(
      `/workspaces/${workspaceAlias}/discord/authenticated`,
      { code, redirect_uri },
    );
    return {
      alias: res.data.alias,
      workspace_id: workspaceAlias,
      name: res.data.guild_info?.name || '',
      import_state: res.data.state,
      history: res.data.history,
      channels: res.data.channels,
      period: res.data.period,
    };
  },
  async getDiscordInfos(alias: string): Promise<DiscordInfo[] | false> {
    try {
      const res = await defaultAxios.get<DiscordWorkspaceResponse[]>(
        `/workspaces/${alias}/discord/`,
      );
      return res.data.map((w) => ({
        alias: w.alias,
        workspace_id: alias,
        name: w.guild_info?.name || '',
        import_state: w.state,
        history: w.history,
        channels: w.channels,
        period: w.period,
      }));
    } catch (e) {
      return false;
    }
  },
  async importWorkspaceFeedback(
    alias: string,
    startDate: string,
    endDate: string,
  ) {
    try {
      const res = await defaultAxios.post(`/discord/${alias}/import`, {
        start_date: startDate,
        end_date: endDate,
      });
      return res.data;
    } catch (e) {
      return false;
    }
  },
  async deleteDiscordInfo(alias: string) {
    try {
      const res = await defaultAxios.delete(`/discord/${alias}`);
      return res.data;
    } catch (e) {
      return false;
    }
  },
  async getDiscordChannels(alias: string): Promise<DiscordChannelMapT> {
    const res = await defaultAxios.get(`/discord/${alias}/channel`);
    return res.data;
  },
  async pushDiscordChannel(alias: string, info: DiscordChannelT[]) {
    try {
      const res = await defaultAxios.patch(`/discord/${alias}/push`, info);
      return res.data;
    } catch (e) {
      return false;
    }
  },
  async pullDiscordChannel(alias: string, info: string[]) {
    try {
      const res = await defaultAxios.patch(`/discord/${alias}/pull`, info);
      return res.data;
    } catch (e) {
      return false;
    }
  },
  async selectAllDiscordChannels(alias: string, infos: DiscordChannelT[]) {
    try {
      const res = await defaultAxios.patch(
        `/discord/${alias}/select_all`,
        infos,
      );
      return res.data;
    } catch (e) {
      return false;
    }
  },
  async deselectAllDiscordChannels(alias: string) {
    try {
      const res = await defaultAxios.patch(`/discord/${alias}/deselect_all`);
      return res.data;
    } catch (e) {
      return false;
    }
  },
  async updateDiscordPeriod(alias: string, period: number) {
    try {
      const res = await defaultAxios.patch(`/discord/${alias}/period`, {
        period,
      });
      return res.data;
    } catch (e) {
      return false;
    }
  },
};

export default discordService;
